import { useEffect, useState } from 'react';
import { AccordionIcon, AccordionItem } from '@chakra-ui/react';
import EditIcon from '@mui/icons-material/Edit';
import { format } from 'date-fns';
import { UseQueryResult } from 'react-query';
import useProfile from '@/hooks/useProfileV2';
import UserInitials from '@/trio-ui/UserInitials';
import { PTORequest, PTOType, Team } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import { isTodayOrTomorrow } from '@/utils/dates';
import { iconByPTOType, ptoTypeLabel } from '@/utils/pto';
import * as Styles from './PTORequestsByTeamItem.styles';
import EditTimeoffRequestModal from '../EditTimeoffRequestModal';

type Props = {
  team: Team;
  ptos: PTORequest[];
  fetchStaffMembers: Record<string, UseQueryResult<StaffMember>>;
  date?: Date;
  onUpdateRequest?: () => void;
};

type PTOsByPTOType = { [type in PTOType]: PTORequest[] };

export default function PTORequestsByTeamItem({
  team,
  ptos,
  fetchStaffMembers,
  date,
  onUpdateRequest,
}: Props): JSX.Element {
  const [editRequest, setEditRequest] = useState<PTORequest>();

  useEffect(() => {
    ptos.forEach((pto) => {
      if (
        pto.id === editRequest?.id &&
        (pto.status !== editRequest?.status || pto.decisionComment !== editRequest?.decisionComment)
      ) {
        setEditRequest(pto);
      }
    });
  }, [ptos, setEditRequest, editRequest]);

  const { profile } = useProfile();
  const [ptosByPTOType, membersSet] = ptos.reduce<[PTOsByPTOType, Set<string>]>(
    ([ptosObject, set], pto) => {
      // condition required due to some pto requests with falsy ptoTypes in the database
      if (pto.ptoType) {
        ptosObject[pto.ptoType].push(pto);
      }

      set.add(pto.staffMemberId);

      return [ptosObject, set];
    },
    [
      {
        [PTOType.GRIEF_PAUSE]: [],
        [PTOType.PRIMARY_CAREGIVER]: [],
        [PTOType.SECONDARY_CAREGIVER]: [],
        [PTOType.HEALTH_PAUSE]: [],
        [PTOType.PERSONAL_PAUSE]: [],
        [PTOType.HEALTH_LEAVE]: [],
      } satisfies PTOsByPTOType,
      new Set<string>(),
    ]
  );

  const memberIds = [...membersSet];

  const customPtoTypeLabel: typeof ptoTypeLabel = {
    ...ptoTypeLabel,
    [PTOType.PERSONAL_PAUSE]: 'Personal Time Off',
  };

  const handleEditRequestAnswer = (request: PTORequest): void => {
    setEditRequest(request);
  };

  const handleCancelPTORequestAnswer = (): void => {
    setEditRequest(undefined);
  };

  const staffMember = editRequest ? fetchStaffMembers[editRequest.staffMemberId]?.data : undefined;
  const isLoadingStaffMember = editRequest
    ? fetchStaffMembers[editRequest.staffMemberId]?.isLoading || false
    : false;

  return (
    <>
      <AccordionItem border="0 none">
        {({ isExpanded }) => (
          <>
            <Styles.Button $isExpanded={isExpanded}>
              <Styles.TeamWrapper>
                <UserInitials firstName={team.name} size={36} fontSize={13} className="shrink-0" />
                <div>
                  <Styles.TeamName>{team.name}</Styles.TeamName>
                  <Styles.TeamMembersCounter>
                    {memberIds.length} team {`${memberIds.length === 1 ? 'member' : 'members'}`} off
                  </Styles.TeamMembersCounter>
                </div>
              </Styles.TeamWrapper>
              <Styles.TeamMembersWrapper>
                {memberIds.map((staffMemberId) => (
                  <UserInitials
                    key={staffMemberId}
                    firstName={fetchStaffMembers[staffMemberId].data?.person.firstName || ''}
                    lastName={fetchStaffMembers[staffMemberId].data?.person.lastName}
                    pictureUrl={
                      fetchStaffMembers[staffMemberId].data?.person.pictureUrl || undefined
                    }
                    fontSize={9}
                    size={24}
                    className="shrink-0"
                  />
                ))}
              </Styles.TeamMembersWrapper>
              <Styles.OptionsWrapper>
                <Styles.ToggleColapse className="toggle-colapse">
                  <span>{isExpanded ? 'Hide details' : 'Show details'}</span>
                  <AccordionIcon />
                </Styles.ToggleColapse>
              </Styles.OptionsWrapper>
            </Styles.Button>
            <Styles.ExpandedPanel>
              {Object.keys(ptosByPTOType)
                .filter((ptoType) => ptosByPTOType[ptoType].length > 0)
                .map((ptoType) => (
                  <div key={ptoType}>
                    <Styles.PTOTypeTitleContainer>
                      {iconByPTOType[ptoType]}
                      <p>{customPtoTypeLabel[ptoType]}</p>
                    </Styles.PTOTypeTitleContainer>
                    <Styles.PTOsTable>
                      <Styles.PTOsTableTitle>{`The following team members from this partner’s team are on ${
                        ptoTypeLabel[ptoType]
                      }${
                        date ? ` ${isTodayOrTomorrow(date) || format(date, 'MMMM d')}` : ''
                      }.`}</Styles.PTOsTableTitle>
                      {ptosByPTOType[ptoType as PTOType].map((pto) => {
                        const customEndingDateLabel = isTodayOrTomorrow(
                          pto.entries[pto.entries.length - 1].date
                        );

                        return (
                          <Styles.PTOListItem key={pto.id}>
                            <div>
                              <UserInitials
                                firstName={
                                  fetchStaffMembers[pto.staffMemberId].data?.person.firstName || ''
                                }
                                lastName={
                                  fetchStaffMembers[pto.staffMemberId].data?.person.lastName
                                }
                                pictureUrl={
                                  fetchStaffMembers[pto.staffMemberId].data?.person.pictureUrl ||
                                  undefined
                                }
                                fontSize={9}
                                size={24}
                                className="shrink-0"
                              />
                              <Styles.PTOListUserName>
                                {fetchStaffMembers[pto.staffMemberId].data?.person.firstName}{' '}
                                {fetchStaffMembers[pto.staffMemberId].data?.person.lastName || ''}
                              </Styles.PTOListUserName>
                            </div>
                            <div>
                              <Styles.PTOTableLabel className="capitalize">
                                {customEndingDateLabel === 'tomorrow'
                                  ? 'Until tomorrow'
                                  : customEndingDateLabel ||
                                    `Until ${format(
                                      pto.entries[pto.entries.length - 1].date,
                                      'MMMM d'
                                    )}`}
                              </Styles.PTOTableLabel>
                            </div>
                            <div>
                              <Styles.RequestAnswerButton
                                onClick={() => handleEditRequestAnswer(pto)}
                              >
                                <EditIcon />
                                Edit Request Answer
                              </Styles.RequestAnswerButton>
                            </div>
                          </Styles.PTOListItem>
                        );
                      })}
                    </Styles.PTOsTable>
                  </div>
                ))}
            </Styles.ExpandedPanel>
          </>
        )}
      </AccordionItem>
      {editRequest && profile.id && (
        <EditTimeoffRequestModal
          isOpen
          request={editRequest}
          onClose={handleCancelPTORequestAnswer}
          managerId={profile.id}
          onUpdateRequest={onUpdateRequest}
          staffMember={staffMember}
          isLoadingStaffMember={isLoadingStaffMember}
        />
      )}
    </>
  );
}
